import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { MapPin, Star, Share2, Globe, TrendingUp, MessageSquare } from 'lucide-react';
import HeroSection from '../components/HeroSection';
import AnimatedImage from '../components/AnimatedImage';
import WaveOverlay from '../components/WaveOverlay';
import houseOrangeImage from '../assets/images/house-orange.png';

const Services = ({ onBookMeeting }) => {
  const services = [
    {
      icon: <MapPin className="w-8 h-8 text-blue-600" />,
      title: "Local SEO",
      description: "Boost your visibility and attract more local customers with our expert local SEO services. From optimizing your website to enhancing your online presence on local directories, we'll help you dominate the search results in your area.",
      features: ["Google My Business optimization", "Local keyword research", "Citation building", "Review management"]
    },
    {
      icon: <Star className="w-8 h-8 text-yellow-500" />,
      title: "Reputation Management",
      description: "Safeguard your online reputation and build trust with potential customers through our comprehensive reputation management services. We'll monitor and manage your online reviews, address any negative feedback, and highlight the positive aspects of your business to enhance your credibility and reputation.",
      features: ["Review monitoring", "Response management", "Reputation repair", "Brand protection"]
    },
    {
      icon: <Share2 className="w-8 h-8 text-pink-500" />,
      title: "Social Marketing",
      description: "Maximize your reach and engagement on social media platforms with our comprehensive social marketing services. Our expert team will develop and execute tailored strategies to increase brand awareness, drive website traffic, and generate leads for your business.",
      features: ["Content creation", "Social media strategy", "Community management", "Paid advertising"]
    },
    {
      icon: <Globe className="w-8 h-8 text-green-600" />,
      title: "Google Business Profile",
      description: "Elevate your online presence with our Google Business Profile management services. Our dedicated team will optimize your profile with accurate information, engaging visuals, and compelling messaging to attract more customers and enhance your visibility in local search results.",
      features: ["Profile optimization", "Photo management", "Post scheduling", "Insights tracking"]
    },
    {
      icon: <MessageSquare className="w-8 h-8 text-orange-500" />,
      title: "Marketing Consultation",
      description: "Gain valuable insights and expert guidance to take your marketing efforts to the next level with our marketing consultation services. From developing a comprehensive marketing strategy to optimizing your campaigns for maximum results, we'll provide personalized recommendations and actionable advice to help you achieve your business goals.",
      features: ["Strategy development", "Campaign optimization", "Performance analysis", "Growth planning"]
    },
    {
      icon: <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>,
      title: "AI Employee",
      description: "Revolutionize your business operations with our AI-powered virtual employees. Our intelligent automation solutions handle repetitive tasks, customer inquiries, and data processing, allowing your team to focus on high-value activities that drive growth and customer satisfaction.",
      features: ["Automated customer support", "Data processing & analysis", "Task automation", "24/7 availability"]
    },
    {
      icon: <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>,
      title: "Workflows & Automations",
      description: "Streamline your business processes with our custom workflow automation solutions. From lead nurturing and follow-up sequences to document processing and appointment scheduling, we create intelligent systems that work 24/7 to enhance efficiency and improve customer experience.",
      features: ["Lead nurturing sequences", "Document processing", "Appointment scheduling", "Process optimization"]
    },
    {
      icon: <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>,
      title: "Custom Solutions",
      description: "Tailored digital marketing packages designed specifically for small and medium businesses. We combine multiple services from our portfolio to create comprehensive solutions that address your unique business needs and budget requirements.",
      features: ["Service bundling", "Custom packages", "Budget optimization", "Scalable solutions"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Digital Marketing Services | Local SEO, AI Automation, Reputation Management</title>
        <meta name="description" content="Explore our digital marketing services: Local SEO, reputation management, social media marketing, Google Business Profile optimization, AI automation, and custom workflow solutions." />
        <link rel="canonical" href="https://insightdigitalagency.com/services" />
        <meta property="og:title" content="Digital Marketing Services | Insight Digital Agency" />
        <meta property="og:description" content="Local SEO, reputation management, AI automation, and custom workflow solutions for small and medium businesses." />
        <meta property="og:url" content="https://insightdigitalagency.com/services" />
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 relative z-40">
            <div className="flex items-center space-x-2 text-orange-300">
              <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                Insight Digital Agency
              </span>
              <span>» Services</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
              Comprehensive Digital Marketing Solutions
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed">
              From local SEO to AI-powered automation, Insight Digital Agency offers a full suite of cutting-edge 
              services and cohesive tools designed to elevate your business and drive measurable results across any industry.
            </p>
            <Button 
              onClick={onBookMeeting}
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 relative z-50"
            >
              Get in Touch
            </Button>
          </div>
          <AnimatedImage 
            src={houseOrangeImage}
            alt="Orange house representing digital solutions"
            type="robot"
            className="!w-full !max-w-3xl !h-auto !transform !scale-[1.8] !translate-x-32"
            containerClassName="!overflow-visible"
          />
        </div>
      </HeroSection>

      {/* Services Introduction */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Our Services</h2>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
              In today's competitive digital landscape, standing out online is not just an advantage—it's a necessity. 
              At Insight Digital Agency, we offer specialized services designed to propel your business to new heights, 
              regardless of your industry.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          {/* First 6 services - 3 columns */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {services.slice(0, 6).map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 flex flex-col">
                <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-6 mx-auto">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2 mb-6 flex-grow">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button className="w-full mt-auto bg-orange-500 hover:bg-orange-600 text-white">
                  Learn More
                </Button>
              </div>
            ))}
          </div>
          
          {/* Last 2 services - centered */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {services.slice(6).map((service, index) => (
              <div key={index + 6} className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 flex flex-col">
                <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-6 mx-auto">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2 mb-6 flex-grow">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button className="w-full mt-auto bg-orange-500 hover:bg-orange-600 text-white">
                  Learn More
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Our Process</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              We follow a proven methodology to ensure your digital marketing success
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery", description: "We analyze your business, goals, and target audience" },
              { step: "02", title: "Strategy", description: "With you, we develop a customized plan of action." },
              { step: "03", title: "Implementation", description: "We execute the plan with precision and care, with frequent, planned updates." },
              { step: "04", title: "Optimization", description: "With regular touchpoints, we monitor, update, and optimize for the best results." }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4 transition-all duration-300 hover:scale-125 hover:shadow-2xl cursor-pointer">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 hero-gradient text-white relative">
        <WaveOverlay inverted={true} color="fill-white" />
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">Ready to Transform Your Online Presence?</h2>
          <p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Let's talk about how we can help your business thrive in today's digital world.
          </p>
          <Button 
            onClick={onBookMeeting}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg"
          >
            Book a Meeting
          </Button>
        </div>
        <WaveOverlay />
      </section>
    </div>
    </>
  );
};

export default Services;

